import { eq } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { PhFilePdf, PhWarningCircle } from 'ember-phosphor-icons';
import type { TOC } from '@ember/component/template-only';
import type { FlagKind } from './document-row.gts';

interface FlaggedBannerSignature {
  Args: {
    filename: string;
    /** The reason text from AI feedback. May be null when AI hasn't provided one. */
    reason: string | null;
    /**
     * "content" — file is the right type but missing required information →
     * offer both `discard` and `use anyways`.
     * "invalid_type" — file is the wrong type / corrupt → offer `discard` only.
     */
    flagKind: FlagKind;
    onDiscard: () => void;
    onUseAnyways: () => void;
  };
  Element: HTMLDivElement;
}

/**
 * Red expanded alert shown beneath a flagged document row. Mirrors the Figma
 * "expanded flagged" frames (4288-2940 and 4330-2660). Two variants:
 *
 *   - content    → "missing a payment amount." | discard | use anyways
 *   - invalid_type → "could not be read." | discard (only)
 */
const FlaggedBanner: TOC<FlaggedBannerSignature> = <template>
  <div
    class="tio-row-top border-l-4 border-red-500 bg-red-50 px-4 py-3 last:rounded-b-xl"
    role="alert"
    data-test-flagged-banner
    ...attributes
  >
    <PhWarningCircle @size="18" @weight="fill" class="mt-0.5 shrink-0 text-red-600" />
    <div class="tio-col-extra-tight tio-fill-min text-sm">
      <div class="flex flex-wrap items-center gap-1.5">
        <PhFilePdf @size="14" class="shrink-0 text-red-700" />
        <span class="truncate font-medium text-red-800">{{@filename}}</span>
        <span class="text-red-700">
          {{#if (eq @flagKind "invalid_type")}}
            {{t "tas.attachments.flag_messages.invalid_type"}}
          {{else if @reason}}
            {{@reason}}
          {{else}}
            {{t "tas.attachments.flag_messages.content_default"}}
          {{/if}}
        </span>
      </div>
    </div>
    <div class="tio-row shrink-0 text-sm">
      {{#unless (eq @flagKind "invalid_type")}}
        <button
          type="button"
          class="font-semibold text-red-800 underline hover:text-red-900"
          data-test-flagged-use-anyways
          {{on "click" @onUseAnyways}}
        >
          {{t "tas.attachments.use_anyways"}}
        </button>
        <span class="text-red-300">|</span>
      {{/unless}}
      <button
        type="button"
        class="font-semibold text-red-800 underline hover:text-red-900"
        data-test-flagged-discard
        {{on "click" @onDiscard}}
      >
        {{t "tas.attachments.discard"}}
      </button>
    </div>
  </div>
</template>;

export default FlaggedBanner;
